// Which Figma account is connected, and every way out of it.
//
// Before this the panel had no exit: once a connection existed the Figma row only expanded and
// collapsed, so an expired token left the user with an account that reported itself connected, an
// import that failed, and nothing to press. Reconnecting and switching accounts are the same
// operation — drop the stored connection, authorise again — so they share one path.
import React, { useState } from "react";
import { AlertTriangle, Loader2, LogOut, RefreshCw, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { FigmaConnection } from "@/hooks/useFigma";

interface FigmaAccountPanelProps {
  connection: FigmaConnection | null;
  onReconnect: () => Promise<void> | void;
  onDisconnect: () => Promise<void> | void;
}

const FigmaAccountPanel: React.FC<FigmaAccountPanelProps> = ({
  connection,
  onReconnect,
  onDisconnect,
}) => {
  const [busy, setBusy] = useState<"reconnect" | "disconnect" | null>(null);

  if (!connection?.connected) return null;

  const needsReconnect = Boolean(connection.needsReconnect);
  const identity = connection.handle || connection.email || "your Figma account";

  const run = async (kind: "reconnect" | "disconnect", action: () => Promise<void> | void) => {
    setBusy(kind);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        {connection.avatarUrl ? (
          <img
            src={connection.avatarUrl}
            alt=""
            className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-soft">
            <Users className="h-3.5 w-3.5 text-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-foreground">{identity}</p>
          {connection.email && connection.handle && (
            <p className="truncate text-[11px] text-muted-foreground">{connection.email}</p>
          )}
        </div>
      </div>

      {needsReconnect && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-900/40 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-amber-900 dark:text-amber-300">
              This Figma connection has expired
            </p>
            <p className="text-[11px] text-amber-800 dark:text-amber-400">
              Imports will fail until you authorise it again. Your designs are untouched.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={needsReconnect ? "default" : "outline"}
          className={`gap-1.5 ${needsReconnect ? "bg-ink-blue-900 text-white hover:bg-ink-blue-800 dark:bg-white/10 dark:hover:bg-white/20" : ""}`}
          disabled={busy !== null}
          onClick={() => run("reconnect", onReconnect)}
        >
          {busy === "reconnect" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {needsReconnect ? "Reconnect Figma" : "Use a different account"}
        </Button>

        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-muted-foreground"
          disabled={busy !== null}
          onClick={() => run("disconnect", onDisconnect)}
        >
          {busy === "disconnect" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <LogOut className="h-3.5 w-3.5" />
          )}
          Disconnect
        </Button>
      </div>

      {/* Figma reuses whatever session the browser already has, so authorising again silently
          returns the same account unless the user signs out there first. Saying so up front avoids
          the loop of pressing this and getting the account they were trying to leave. */}
      <p className="text-[11px] text-muted-foreground">
        Figma signs you in with the account already open in this browser. To use a different one,
        sign out of Figma first, then press the button above.
      </p>
    </div>
  );
};

export default FigmaAccountPanel;
